import { useState, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Save, Trash2, BarChart3, LineChart, PieChart, Table as TableIcon, Hash, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import { EditableTitle } from './EditableTitle';

const CHART_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  TABLE: TableIcon,
  BAR: BarChart3,
  LINE: LineChart,
  PIE: PieChart,
  SINGLE_VALUE: Hash,
};

interface ChartTopBarProps {
  chartName?: string;
  chartType?: string;
  chartData?: Record<string, any>[];
  chartConfig?: any;
  dateRange?: string;
  customDateStart?: string;
  customDateEnd?: string;
  onDateRangeChange?: (dateRange: string, customStart?: string, customEnd?: string) => void;
  hasUnsavedChanges?: boolean;
  onSave?: () => void;
  isSaving?: boolean;
  onRename?: (newName: string) => void | Promise<void>;
  onDelete?: () => Promise<void>;
}

export const ChartTopBar: FC<ChartTopBarProps> = ({
  chartName = 'Untitled Chart',
  chartType,
  hasUnsavedChanges = false,
  onSave,
  isSaving = false,
  onRename,
  onDelete,
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const chartTypeIcon = chartType ? (() => {
    const IconComponent = CHART_TYPE_ICONS[chartType] || BarChart3;
    return <IconComponent className="h-5 w-5 text-muted-foreground" />;
  })() : null;

  return (
    <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background">
      {/* Left side: Breadcrumbs and Chart name */}
      <div className="flex flex-col gap-2 flex-1 min-w-0">
        {/* Breadcrumbs */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/analytics">Analytics</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{chartName}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        {/* Chart name */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {chartTypeIcon && (
            <div className="flex-shrink-0">
              {chartTypeIcon}
            </div>
          )}
          {onRename ? (
            <EditableTitle
              title={chartName}
              onSave={onRename}
              className="flex-1 min-w-0"
              titleClassName="truncate"
            />
          ) : (
            <h1 className="text-xl font-semibold text-foreground truncate">{chartName}</h1>
          )}
        </div>
      </div>

      {/* Right side: Actions */}
      <div className="flex items-center gap-2 ml-4">
        {/* Delete Button */}
        {onDelete && (
          <>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Chart</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete <strong>{chartName}</strong>? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={isDeleting}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={async (e) => {
                      e.preventDefault();
                      setIsDeleting(true);
                      try {
                        await onDelete();
                      } finally {
                        setIsDeleting(false);
                        setShowDeleteDialog(false);
                      }
                    }}
                  >
                    {isDeleting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
        {/* Save Button */}
        {onSave && (
          <Button
            variant={hasUnsavedChanges ? "default" : "outline"}
            onClick={onSave}
            disabled={!hasUnsavedChanges || isSaving}
            className={hasUnsavedChanges ? "bg-primary text-primary-foreground" : ""}
          >
            <Save className="h-4 w-4 mr-2" />
            Save
          </Button>
        )}
      </div>
    </div>
  );
};
